import { HttpErrorResponse } from '@angular/common/http';
import {
  AfterContentInit,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { NGXLogger } from 'ngx-logger';
import { firstValueFrom, Subscription } from 'rxjs';
import {
  BookingService,
  FormUtilsService,
  NotificationService,
} from 'src/app/services';
import { EventBooking, SelectOption } from 'src/app/types';
import { ExamBookingAddQuestionsAnswersRequest } from 'src/app/types/request/ExamBookingAddQuestionsAnswersRequest';
import { ItbBusinessVariant } from '../../../../../types/AdditionalQuestionData';

@Component({
  selector: 'app-step4-additional-questions-form',
  templateUrl:
    './step4-additional-questions-form.component.html',
  styleUrls: [
    './step4-additional-questions-form.component.scss',
  ],
  encapsulation: ViewEncapsulation.None,
})
export class Step4AdditionalQuestionsFormComponent
  implements
    OnInit,
    AfterContentInit,
    AfterViewInit,
    OnDestroy
{
  @Output()
  additionalQuestionsFormValid: EventEmitter<boolean> =
    new EventEmitter<boolean>();
  @Output() stepLoaded: EventEmitter<void> =
    new EventEmitter<void>();

  public readonly selectItbBusinessVariantOptions: SelectOption[] =
    [
      {
        value: 'basic',
        label:
          'itb-business.questions.variant.options.basic.complete',
      },
      {
        value: 'plus',
        label:
          'itb-business.questions.variant.options.plus.complete',
      },
    ];
  confirmSendResultsOption: SelectOption[] = [
    {
      value: 1,
      label: 'tm-wiso.questions.confirm-send-results.yes',
    },
    {
      value: 2,
      label: 'tm-wiso.questions.confirm-send-results.no',
    },
  ];
  form!: FormGroup;
  languageOptions: SelectOption[] = [];
  sendResultsToUniversitiesOptions: SelectOption[] = [];
  universityOptions: SelectOption[] = [];

  private allSendResultsToUniversitiesOptions: SelectOption[] =
    [];
  private unsubOnDestroy: Subscription = new Subscription();

  constructor(
    private bookingService: BookingService,
    private changeDetectorRef: ChangeDetectorRef,
    private formBuilder: FormBuilder,
    private formUtils: FormUtilsService,
    private logger: NGXLogger,
    private notificationService: NotificationService,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.getAdditionalBookingQuestions(
      this.booking.bookingId
    );

    this.form = this.formBuilder.group({
      // Relevant for TM-WISO/GTEBS, ITB-Business, ITB-Technology
      applyForUniversity: [''],
      // Relevant for ITB-Business
      selectItbBusinessVariant: [''],
      // Relevant for TM-WISO/GTEBS, ITB-Business, ITB-Technology
      confirmSendResults: [''],
      // Relevant for TM-WISO/GTEBS
      currentStudySubject: [''],
      // Relevant for TM-WISO/GTEBS
      currentTypeOfDegree: [''],
      // Relevant for TM-WISO/GTEBS
      currentUniversityName: [''],
      // Relevant for TM-WISO/GTEBS
      currentUniversityPlace: [''],
      // Relevant for PhaST, TM-WISO/GTEBS, ITB-Business, ITB-Science, ITB-Technology
      finalGrade: [''],
      // Relevant for TM-WISO/GTEBS
      graduateDegree: [''],
      // Relevant for TM-WISO/GTEBS, GSAT, ITB-Business, ITB-Science, ITB-Technology
      language: [''],
      // Relevant for TM-WISO/GTEBS, ITB-Business, ITB-Technology
      sendResultsToUniversities: [''],
      // Relevant for PhaST
      university: [''],
    });

    this.unsubOnDestroy.add(
      this.applyForUniversity.valueChanges.subscribe(() => {
        this.syncAutoSendUniversitiesWithApplicationUniversities();
      })
    );

    // Add validators based on the test template
    switch (
      this.bookingService.formState.item.test.templateName
    ) {
      case 'PhaST': {
        // No required fields
        break;
      }
      case 'ITB-Business': {
        this.applyForUniversity.addValidators(
          Validators.required
        );
        this.selectItbBusinessVariant.addValidators(
          Validators.required
        );
        this.confirmSendResults.addValidators(
          Validators.required
        );
        this.language.addValidators(Validators.required);
        break;
      }
      case 'ITB-Science': {
        this.language.addValidators(Validators.required);
        break;
      }
      case 'ITB-Technology': {
        this.applyForUniversity.addValidators(
          Validators.required
        );
        this.confirmSendResults.addValidators(
          Validators.required
        );
        this.language.addValidators(Validators.required);
        break;
      }
      case 'TM-WISO': {
        this.applyForUniversity.addValidators(
          Validators.required
        );
        this.confirmSendResults.addValidators(
          Validators.required
        );
        this.language.addValidators(Validators.required);
        break;
      }
      case 'GSAT': {
        this.language.addValidators(Validators.required);
        break;
      }
      // Not handling the SdV case here, since that will never happen in this form.
    }

    this.unsubOnDestroy.add(
      this.translateService.onLangChange.subscribe(() => {
        this.sortUniversitiesByTranslation(
          this.universityOptions
        );
        this.sortUniversitiesByTranslation(
          this.sendResultsToUniversitiesOptions
        );
      })
    );
  }

  ngAfterContentInit() {
    this.stepLoaded.emit();
  }

  ngAfterViewInit(): void {
    this.unsubOnDestroy.add(
      this.form.valueChanges.subscribe((_value) => {
        this.additionalQuestionsFormValid.emit(
          this.form.valid
        );
      })
    );
    this.changeDetectorRef.detectChanges();
  }

  async submit(): Promise<boolean> {
    this.form.markAllAsTouched();
    if (!this.form.valid) {
      return false;
    }

    let requestBody: ExamBookingAddQuestionsAnswersRequest;

    switch (this.booking.test.templateName) {
      case 'PhaST':
        requestBody = {
          universityIds: this.university.value || [],
          finalGrade:
            this.finalGrade.value?.trim() || undefined,
        };
        break;

      case 'TM-WISO':
        requestBody = {
          finalGrade:
            this.finalGrade.value?.trim() || undefined,
          language:
            this.formUtils.unwrapSingleSelect(
              this.language.value
            ) || undefined,
          sendResultsToUniversityIds:
            this.confirmSendResults.value === 1 &&
            this.sendResultsToUniversities.value?.length
              ? this.sendResultsToUniversities.value
              : undefined,
          studiesDetails: {
            graduateDegree:
              this.graduateDegree.value?.trim() ||
              undefined,
            subject:
              this.currentStudySubject.value || undefined,
            typeOfDegree:
              this.currentTypeOfDegree.value || undefined,
          },
          universityDetails: {
            name:
              this.currentUniversityName.value || undefined,
            city:
              this.currentUniversityPlace.value ||
              undefined,
          },
          universityIds: this.applyForUniversity
            .value as number[],
        };
        break;

      case 'GSAT':
        requestBody = {
          language:
            this.formUtils.unwrapSingleSelect(
              this.language.value
            ) || undefined,
        };
        break;

      case 'ITB-Business':
        requestBody = {
          // TODO Marked for removal with BOTI 1.5.0
          language:
            this.formUtils.unwrapSingleSelect(
              this.language.value
            ) || undefined,
          universityIds: this.applyForUniversity
            .value as number[],
          sendResultsToUniversityIds:
            this.confirmSendResults.value === 1 &&
            this.sendResultsToUniversities.value?.length
              ? this.sendResultsToUniversities.value
              : undefined,
          finalGrade:
            this.finalGrade.value?.trim() || undefined,
          testDetails: {
            itbBusinessVariant:
              this.selectItbBusinessVariant.value ||
              undefined,
            language:
              this.formUtils.unwrapSingleSelect(
                this.language.value
              ) || undefined,
          },
        };
        break;

      case 'ITB-Science':
        requestBody = {
          language:
            this.formUtils.unwrapSingleSelect(
              this.language.value
            ) || undefined,
          finalGrade:
            this.finalGrade.value?.trim() || undefined,
        };
        break;

      case 'ITB-Technology':
        requestBody = {
          language:
            this.formUtils.unwrapSingleSelect(
              this.language.value
            ) || undefined,
          universityIds:
            this.applyForUniversity.value || [],
          sendResultsToUniversityIds:
            this.confirmSendResults.value === 1 &&
            this.sendResultsToUniversities.value?.length
              ? this.sendResultsToUniversities.value
              : undefined,
          finalGrade:
            this.finalGrade.value?.trim() || undefined,
        };
        break;

      default:
        requestBody = {
          universityIds: [],
        };
    }

    this.booking.additionalQuestionData = requestBody;

    try {
      await this.bookingService.updateBooking(
        this.booking.bookingId
      );
      return true;
    } catch (error) {
      await this.fail(error as HttpErrorResponse);
      return false;
    }
  }

  ngOnDestroy(): void {
    this.unsubOnDestroy.unsubscribe();
  }

  private getAdditionalBookingQuestions(
    bookingId: number
  ): void {
    this.bookingService
      .loadAdditionalBookingQuestions(bookingId)
      .then((additionalBookingQuestions) => {
        if (additionalBookingQuestions.languages) {
          this.languageOptions =
            additionalBookingQuestions.languages.map(
              (lang) => ({
                value: lang,
                label: lang,
              })
            );
        }

        [
          ...(additionalBookingQuestions.universities ||
            []),
          ...(additionalBookingQuestions.sendResultsToUniversities ||
            []),
        ].forEach(({ translationKey, translations }) => {
          for (let language in translations) {
            this.translateService.setTranslation(
              language,
              { [translationKey]: translations[language] },
              true
            );
          }
        });

        if (
          additionalBookingQuestions.sendResultsToUniversities
        ) {
          this.allSendResultsToUniversitiesOptions =
            additionalBookingQuestions.sendResultsToUniversities.map(
              ({ id, translationKey }) => ({
                value: id,
                label: translationKey,
              })
            );
          this.syncAutoSendUniversitiesWithApplicationUniversities();
        }

        if (additionalBookingQuestions.universities) {
          this.universityOptions =
            additionalBookingQuestions.universities.map(
              ({ id, translationKey }) => ({
                value: id,
                label: translationKey,
              })
            );
        }
      })
      .then(() => {
        this.sortUniversitiesByTranslation(
          this.universityOptions
        ).then((sortedOptions) => {
          this.universityOptions = sortedOptions;
        });

        this.sortUniversitiesByTranslation(
          this.allSendResultsToUniversitiesOptions
        ).then((sortedOptions) => {
          this.allSendResultsToUniversitiesOptions =
            sortedOptions;
          this.syncAutoSendUniversitiesWithApplicationUniversities();
        });
      })
      .catch(() => this.fail);
  }

  private syncAutoSendUniversitiesWithApplicationUniversities(): void {
    const applicationUniversityIds = new Set(
      this.applyForUniversity.value || []
    );
    this.sendResultsToUniversitiesOptions =
      this.allSendResultsToUniversitiesOptions.filter(
        ({ value }) =>
          typeof value === 'number' &&
          applicationUniversityIds.has(value)
      );

    const selectedAutoSendUniversityIds =
      this.sendResultsToUniversities.value || [];
    const validAutoSendUniversityIds =
      selectedAutoSendUniversityIds.filter((id) =>
        applicationUniversityIds.has(id)
      );
    if (
      validAutoSendUniversityIds.length !==
      selectedAutoSendUniversityIds.length
    ) {
      this.sendResultsToUniversities.setValue(
        validAutoSendUniversityIds
      );
    }
  }

  private async sortUniversitiesByTranslation(
    universityOptions: SelectOption[]
  ): Promise<SelectOption[]> {
    const translatedUniversityOptions = await Promise.all(
      universityOptions.map(async (selectOption) => {
        const translation = await firstValueFrom(
          this.translateService.get(selectOption.label)
        );
        return {
          selectOption,
          translation,
        };
      })
    );

    universityOptions = translatedUniversityOptions
      .sort((first, second) =>
        (first.translation as string).localeCompare(
          second.translation as string
        )
      )
      .map(({ selectOption }) => selectOption);

    return universityOptions;
  }

  private fail(
    error: HttpErrorResponse
  ): Promise<undefined> {
    this.logger.error(
      'get additional booking questions failed',
      { error }
    );

    const errorDialog =
      this.notificationService.httpError(error);
    return errorDialog.finally(() => Promise.reject(error));
  }

  get booking(): EventBooking {
    return this.bookingService.formState.item;
  }

  get university(): FormControl<number[] | null> {
    return this.form.get('university') as FormControl<
      number[] | null
    >;
  }

  get finalGrade(): FormControl<string | null> {
    return this.form.get('finalGrade') as FormControl<
      string | null
    >;
  }

  get language(): FormControl<string | null> {
    return this.form.get('language') as FormControl<
      string | null
    >;
  }

  get applyForUniversity(): FormControl<number[] | null> {
    return this.form.get(
      'applyForUniversity'
    ) as FormControl<number[] | null>;
  }

  get currentUniversityName(): FormControl<string | null> {
    return this.form.get(
      'currentUniversityName'
    ) as FormControl<string | null>;
  }

  get currentUniversityPlace(): FormControl<string | null> {
    return this.form.get(
      'currentUniversityPlace'
    ) as FormControl<string | null>;
  }

  get currentStudySubject(): FormControl<string | null> {
    return this.form.get(
      'currentStudySubject'
    ) as FormControl<string | null>;
  }

  get currentTypeOfDegree(): FormControl<string | null> {
    return this.form.get(
      'currentTypeOfDegree'
    ) as FormControl<string | null>;
  }

  get graduateDegree(): FormControl<string | null> {
    return this.form.get('graduateDegree') as FormControl<
      string | null
    >;
  }

  get confirmSendResults(): FormControl<number | null> {
    return this.form.get(
      'confirmSendResults'
    ) as FormControl<number | null>;
  }

  get sendResultsToUniversities(): FormControl<
    number[] | null
  > {
    return this.form.get(
      'sendResultsToUniversities'
    ) as FormControl<number[] | null>;
  }

  get selectItbBusinessVariant(): FormControl<ItbBusinessVariant | null> {
    return this.form.get(
      'selectItbBusinessVariant'
    ) as FormControl<ItbBusinessVariant | null>;
  }
}
